import { ReactNode, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import LargePopupModalContainer from "../../Containers/LargePopupModalContainer";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { Colors } from "../../../utils/colors";
import SearchBoxContainer from "../../Containers/SearchBoxContainer";
import ListTabs from "../../DropdownsAndTabs/ListTabs";
import { MassTextsActionItem } from "../MassTexts/Items/MassTextsActionItem";
import { Icon } from "@iconify/react";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { Event, EventType, Visibility } from "@markit/common.types";
import VirtuosoList from "../../Lists/VirtuosoList";
import {
  GetDateWithTime,
  availableProductsTypes,
  foundUserProducts,
  isEventRelatedType,
  isProductNonPostType,
  getThumbnailImageUrl,
} from "@markit/common.utils";
import EventItemImage from "../../DisplayItem/EventItem/EventItemImage";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import SelectProductsList from "./SelectProductsList";
import filter from "lodash.filter";
import { getEventState } from "../../../redux/slices/eventSlice";
import CreateFormAndTrackingLinkModal from "../FormSubmissions/CreateFormAndTrackingLinkModal";
import AttachmentSelectItemList from "../MassTexts/Message/AttachmentSelectItemList";

type SelectProductItemModalProps = {
  headerComp: ReactNode;
  generalTabComp: ReactNode;
  extraStepComp?: ReactNode;
  items: string[]; // The ID of the selected item(s)
  setItems: (newItems: string) => void;
  selectedEventType: EventType | undefined;
  setSelectedEventType: (selectedEventType: EventType | undefined) => void;
  userEvents: Event[];
  listView: number;
  setListView: (listView: number) => void;
  showHiddenLabel: boolean;
};

// Side Panel that displays the list of products to select an item(s) from
const SelectProductItemModal = (props: SelectProductItemModalProps) => {
  const {
    headerComp,
    generalTabComp,
    extraStepComp,
    items,
    setItems,
    selectedEventType,
    setSelectedEventType,
    userEvents,
    listView,
    setListView,
    showHiddenLabel,
  } = props;
  const { accountData } = useSelector(getAccountState).account;
  const { events, tickets } = useSelector(getEventState).events;
  const [searchTerm, setSearchTerm] = useState("");
  const [stateValue, setStateValue] = useState(0);
  const [newTrackingLinkModal, setNewTrackingLinkModal] = useState(false);

  const styles = {
    eventItemContainer: {
      border: "none",
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
    },
  };

  const tabLabels = useMemo(() => {
    return ["General", "Links", "Forms", "Posts", "Products", "Events"];
  }, []);

  const userItems = useMemo(
    () =>
      foundUserProducts(
        userEvents,
        accountData.uid,
        listView === 1
          ? EventType.GENERIC_LINK_NO_DATA
          : listView === 2
            ? EventType.GENERIC_LINK
            : listView === 3
              ? EventType.POST
              : selectedEventType || EventType.MARKIT,
        selectedEventType && stateValue === 1 ? "inactive" : "active",
        undefined,
        tickets
      ).sort((a, b) => {
        if (selectedEventType && isEventRelatedType(selectedEventType)) {
          return (
            new Date(stateValue === 0 ? a.start : b.start).getTime() -
            new Date(stateValue === 0 ? b.start : a.start).getTime()
          );
        }
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }),
    [accountData.uid, listView, selectedEventType, stateValue, userEvents, tickets]
  );

  const handleSearch = useCallback((text: string) => {
    setSearchTerm(text.toLowerCase());
  }, []);

  const containsItem = useCallback((item: Event, query: string) => {
    return item.title.toLowerCase().includes(query);
  }, []);

  const itemsToShow: Event[] = useMemo(() => {
    let itemList: Event[] = userItems;
    itemList = filter(itemList, (item: Event) => {
      return containsItem(item, searchTerm.toLowerCase());
    });
    return itemList;
  }, [userItems, searchTerm, containsItem]);

  const ActionItemDivider = useMemo(
    () => (
      <div style={{ paddingLeft: 62 }}>
        <HorizontalDivider altMargin={7} />
      </div>
    ),
    []
  );

  const renderCreateNewLink = useMemo(
    () => (
      <>
        <MassTextsActionItem
          title={"Create New Link"}
          icon={
            <Icon
              icon="mdi:link-variant-plus"
              height={29}
              style={{ color: Colors.BLACK }}
            />
          }
          onPress={() => setNewTrackingLinkModal(true)}
          alternateRightIcon={
            <Icon
              icon="ion:add-circle"
              height={22}
              style={{ color: Colors.BLACK }}
            />
          }
        />
        {ActionItemDivider}
      </>
    ),
    [ActionItemDivider]
  );

  const renderHiddenLabel = useMemo(
    () =>
      showHiddenLabel ? (
        <div className="AlignedRow" style={{ gap: 4 }}>
          <Icon icon="ion:eye" height={14} style={{ color: Colors.ORANGE1 }} />
          <span style={{ color: Colors.ORANGE1 }}>Hidden</span>
        </div>
      ) : null,
    [showHiddenLabel]
  );

  return (
    <>
      <LargePopupModalContainer
        showModal={true}
        preventContainerScroll
        headerComp={headerComp}
        valueComp={
          <div
            className="ColumnNormal PreventScroll"
            style={{ height: "100%", gap: 10, flex: 1 }}
          >
            {extraStepComp || (
              <>
                <div className="ColumnNormal" style={{ gap: 10 }}>
                  <SearchBoxContainer
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    containerStyles={{ marginTop: 0 }}
                  />
                  {selectedEventType ? (
                    <ListTabs
                      tabLabels={["Active", "Inactive"]}
                      tabNumbers={[-1, -1]}
                      selectedValue={stateValue}
                      onChange={setStateValue}
                      altColor={Colors.BLACK}
                    />
                  ) : (
                    <ListTabs
                      tabLabels={tabLabels}
                      tabNumbers={Array(tabLabels.length).fill(-1)}
                      selectedValue={listView}
                      onChange={setListView}
                      altColor={Colors.BLACK}
                    />
                  )}
                </div>
                <div
                  className="ColumnNormal HideScrollbar"
                  style={{ height: "calc(100vh - 200px)", overflow: "auto" }}
                >
                  {selectedEventType ? (
                    <AttachmentSelectItemList
                      items={items}
                      setItems={setItems}
                      selectedEventType={selectedEventType}
                      itemsToShow={itemsToShow}
                      stateValue={stateValue}
                      searchTerm={searchTerm}
                      showHiddenLabel={showHiddenLabel}
                      isTicketLink={false}
                    />
                  ) : listView === 0 ? (
                    generalTabComp
                  ) : listView === 1 || listView === 2 || listView === 3 ? (
                    <VirtuosoList
                      items={itemsToShow}
                      loadMoreOnPress={() => {}}
                      renderItem={(item: Event) => {
                        // Populate with mediaUrl from events if not present in item (created new link within here)
                        const foundItemImage =
                          getThumbnailImageUrl(item) ||
                          getThumbnailImageUrl(
                            events.find((event) => event.id === item.id)
                          );
                        const finalItem: Event = {
                          ...item,
                          photoURL: foundItemImage,
                        };
                        return (
                          <div>
                            <MassTextsActionItem
                              title={item.title}
                              subtext={
                                <div
                                  className="ColumnNormal"
                                  style={{ gap: 4 }}
                                >
                                  <span>
                                    Created:{" "}
                                    {GetDateWithTime(item.createdAt, true)}
                                  </span>
                                  {item.visibility === Visibility.Private
                                    ? renderHiddenLabel
                                    : null}
                                </div>
                              }
                              icon={
                                <EventItemImage
                                  item={finalItem}
                                  size={48}
                                  containerStyles={styles.eventItemContainer}
                                />
                              }
                              onPress={() => setItems(item.id)}
                              isCheckSelected={items.includes(item.id)}
                              checkColor={Colors.BLACK}
                            />
                            {ActionItemDivider}
                          </div>
                        );
                      }}
                      isLoading={false}
                      isFinished={true}
                      searchTerm={searchTerm}
                      renderNonSearchEmptyView={
                        <div className="ColumnNormal">
                          {listView === 1 ? renderCreateNewLink : null}
                          <EmptyStateButton
                            title={`No ${
                              listView === 1
                                ? "Links"
                                : listView === 2
                                  ? "Forms"
                                  : "Posts"
                            }`}
                            description={"Your items will show up here"}
                            icon={
                              <Icon
                                icon={
                                  listView === 1
                                    ? "mdi:link-variant"
                                    : listView === 2
                                      ? "ion:document-text"
                                      : "ion:newspaper"
                                }
                                height={64}
                                style={{ color: Colors.GRAY1 }}
                              />
                            }
                            iconBox={84}
                            containerStyles={{ paddingTop: 100 }}
                          />
                        </div>
                      }
                      renderHeader={listView === 1 ? renderCreateNewLink : null}
                    />
                  ) : (
                    <SelectProductsList
                      onItemPress={setSelectedEventType}
                      productTypesToDisplay={availableProductsTypes.filter(
                        (type) =>
                          listView === 4
                            ? isProductNonPostType(type)
                            : isEventRelatedType(type)
                      )}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        }
      />
      {newTrackingLinkModal ? (
        <CreateFormAndTrackingLinkModal
          isVisible={newTrackingLinkModal}
          setIsVisible={setNewTrackingLinkModal}
          eventType={EventType.GENERIC_LINK_NO_DATA}
          onConfirmation={(linkId: string) => {
            setItems(linkId);
            if (listView !== 1) {
              setListView(1);
            }
          }}
          defaultPublicLink
        />
      ) : null}
    </>
  );
};

export default SelectProductItemModal;
